
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useChat } from "@/hooks/useChat";
import { useOptimizedChatSearch } from "@/hooks/useOptimizedChatSearch";
import { realtimeCentral } from "@/core/RealtimeCentral";
import { chatDataService } from "@/core/services/ChatDataService";
import { toast } from "sonner";
import { useQueryClient } from '@tanstack/react-query';
import { cn } from "@/lib/utils";
import { OnboardingGuard } from "@/components/guards/OnboardingGuard";

// Components
import { ChatsHeader } from "@/components/chat/ChatsHeader";
import { SearchBar } from "@/components/chat/SearchBar";
import { ChatListItem } from "@/components/chat/ChatListItem";
import { ChatLoadingState } from "@/components/chat/LoadingState";
import { EmptyChats } from "@/components/chat/EmptyChats";
import { ChatErrorState } from "@/components/chat/ErrorState";
import { debugLogger } from '@/utils/debugLogger';

interface ChatListProps {
  variant?: 'standalone' | 'sidebar';
  showHeader?: boolean;
  className?: string;
}

const ChatList = ({ 
  variant = 'standalone', 
  showHeader = true, 
  className 
}: ChatListProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState<string | null>(null);
  const [showDeletedChats, setShowDeletedChats] = useState(false);
  const queryClient = useQueryClient();

  // Use the chat hook for regular conversations and profiles
  const {
    conversations,
    conversationsLoading,
    error: conversationsError,
    refetchConversations
  } = useChat();

  // NOVO: Separar conversas ativas e deletadas
  const { activeConversations, deletedConversations } = useMemo(() => {
    const active = conversations?.filter(conv => !conv.isDeleted) || [];
    const deleted = conversations?.filter(conv => conv.isDeleted) || [];
    
    return {
      activeConversations: active,
      deletedConversations: deleted
    };
  }, [conversations]);

  // Extract profiles from active conversations for search optimization
  const profiles = useMemo(() => {
    if (!activeConversations?.length) return [];
    const profilesSet = new Set();
    const profilesList: Array<{
      id: string;
      username: string;
      main_photo_url: string;
      hidden: boolean;
      onboarding_completed: boolean;
    }> = [];
    activeConversations.forEach(conv => {
      if (conv.partner && !profilesSet.has(conv.partner.id)) {
        profilesSet.add(conv.partner.id);
        profilesList.push({
          id: conv.partner.id,
          username: conv.partner.username || '',
          main_photo_url: conv.partner.avatar || '',
          hidden: conv.partner.hidden || false,
          onboarding_completed: true // Already filtered in useChat
        });
      }
    });
    return profilesList;
  }, [activeConversations]);

  // Use optimized search that reuses existing data (only active conversations)
  const {
    searchTerm,
    setSearchTerm,
    searchResults,
    searchLoading,
    isSearching
  } = useOptimizedChatSearch({
    conversations: activeConversations || [],
    profiles
  });

  debugLogger.debug('[ChatList] Current state:', {
    searchTerm,
    activeConversationsCount: activeConversations?.length || 0,
    deletedConversationsCount: deletedConversations?.length || 0,
    searchResultsCount: searchResults?.length || 0,
    isSearching
  });

  // Handle conversation deletion with improved invalidation
  const handleDeleteConversation = async (conversationId: string) => {
    if (!user?.id || isDeleting) return;

    try {
      setIsDeleting(conversationId);
      debugLogger.debug('[ChatList] Starting conversation deletion:', conversationId);
      
      const success = await chatDataService.deleteConversation(conversationId, user.id);
      
      if (success) {
        debugLogger.debug('[ChatList] Conversation deletion successful:', conversationId);
        toast.success('Conversa excluída com sucesso');
        
        // Invalidate and refetch multiple related queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['conversations', user.id] }),
          queryClient.invalidateQueries({ queryKey: ['messages'] }),
          queryClient.invalidateQueries({ queryKey: ['message-search'] }),
        ]);
        
        // Force refetch to ensure sync with database
        await refetchConversations();
        
        debugLogger.debug('[ChatList] All queries invalidated and refetched');
      } else {
        debugLogger.error('[ChatList] Conversation deletion failed:', conversationId);
        toast.error('Erro ao excluir conversa. Tente novamente.');
      }
    } catch (error) {
      debugLogger.error('[ChatList] Error deleting conversation:', error);
      toast.error('Erro ao excluir conversa. Tente novamente.');
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle conversation restoration
  const handleRestoreConversation = async (conversationId: string) => {
    if (!user?.id || isRestoring) return;

    try {
      setIsRestoring(conversationId);
      debugLogger.debug('[ChatList] Starting conversation restoration:', conversationId);
      
      const success = await chatDataService.restoreConversation(conversationId, user.id);
      
      if (success) {
        debugLogger.debug('[ChatList] Conversation restoration successful:', conversationId);
        toast.success('Conversa restaurada com sucesso');
        
        // Invalidate and refetch multiple related queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['conversations', user.id] }),
          queryClient.invalidateQueries({ queryKey: ['messages'] }),
          queryClient.invalidateQueries({ queryKey: ['message-search'] }),
        ]);
        
        // Force refetch to ensure sync with database
        await refetchConversations();
        
        debugLogger.debug('[ChatList] All queries invalidated and refetched after restoration');
      } else {
        debugLogger.error('[ChatList] Conversation restoration failed:', conversationId);
        toast.error('Erro ao restaurar conversa. Tente novamente.');
      }
    } catch (error) {
      debugLogger.error('[ChatList] Error restoring conversation:', error);
      toast.error('Erro ao restaurar conversa. Tente novamente.');
    } finally {
      setIsRestoring(null);
    }
  };

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (!user && !conversationsLoading) {
      navigate('/auth');
    }
  }, [user, conversationsLoading, navigate]);

  // Fallback para detectar perda de sessão após inatividade
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!user && !conversationsLoading && !window.location.pathname.includes('/auth')) {
        debugLogger.warn('ChatList', 'Session lost after inactivity, redirecting to auth');
        navigate('/auth', {
          replace: true
        });
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [user, conversationsLoading, navigate]);

  // Initialize realtime for conversation updates
  useEffect(() => {
    if (!user?.id) return;
    realtimeCentral.initialize(user.id);
    const unsubscribe = realtimeCentral.subscribe(crypto.randomUUID(), {
      id: crypto.randomUUID(),
      onMessage: data => {
        if (data.message) {
          refetchConversations();
        }
      },
      onNotification: () => {
        refetchConversations();
      }
    });
    return unsubscribe;
  }, [user?.id, refetchConversations]);

  // Convert data to display format - USANDO SEPARAÇÃO DE CONVERSAS
  const displayItems = useMemo(() => {
    if (isSearching) {
      // Convert search results to ChatListItem format (só conversas ativas)
      return searchResults.map(result => ({
        id: result.conversationId,
        uniqueKey: result.messageId ? `${result.conversationId}-${result.messageId}` : result.conversationId,
        partner: {
          id: result.partnerId,
          name: result.partnerUsername,
          username: result.partnerUsername,
          avatar: result.partnerAvatar
        },
        lastMessage: result.matchedMessage || null,
        time: result.lastMessageTime,
        unread: result.unreadCount,
        isSearchResult: true,
        searchTerm,
        matchType: result.matchType,
        isDeleted: false // Search só retorna conversas ativas
      }));
    } else {
      // Mostrar apenas conversas ativas por padrão
      if (!showDeletedChats) {
        return activeConversations.map(conv => ({ 
          ...conv, 
          uniqueKey: conv.id, 
          isSearchResult: false 
        }));
      }
      
      // Quando mostrar excluídas, mostrar apenas conversas deletadas
      return deletedConversations.map(conv => ({ 
        ...conv, 
        uniqueKey: conv.id, 
        isSearchResult: false 
      }));
    }
  }, [activeConversations, deletedConversations, searchResults, isSearching, searchTerm, showDeletedChats]);

  const handleConversationClick = (conversationId: string) => {
    navigate(`/main/chat/${conversationId}`);
  };

  const handleRetry = () => {
    refetchConversations();
  };

  if (conversationsLoading && !user) {
    return <ChatLoadingState />;
  }

  if (conversationsError && !conversations?.length) {
    return <ChatErrorState title="Erro ao carregar conversas" description={conversationsError.message || "Erro desconhecido"} />;
  }

  // Define responsive styles based on variant
  const containerClasses = cn(
    "h-screen flex flex-col overflow-hidden",
    variant === 'sidebar' && "bg-black",
    variant === 'standalone' && "bg-transparent",
    className
  );

  const searchContainerClasses = cn(
    "flex-shrink-0",
    variant === 'standalone' ? "px-4 pt-4 pb-2" : "px-3 pt-3 pb-2"
  );

  const scrollContainerClasses = cn(
    "flex-1 overflow-y-auto",
    variant === 'standalone' ? "px-4 pt-2 pb-24 md:pb-8" : "px-3 pt-2 pb-4"
  );

  const listClasses = cn(
    "space-y-2 transition-opacity duration-200",
    variant === 'standalone' ? "pb-4" : "pb-4",
    isSearching && searchLoading ? 'opacity-70' : 'opacity-100'
  );

  return (
    <OnboardingGuard>
      <div className={containerClasses}>
      {showHeader && (
        <ChatsHeader 
          showDeletedChats={showDeletedChats}
          onToggleDeletedChats={() => setShowDeletedChats(!showDeletedChats)}
        />
      )}
      
      <div className={searchContainerClasses}>
        <SearchBar 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
          isSearching={isSearching && searchLoading}
          variant={variant}
        />
      </div>

      <div className={scrollContainerClasses}>
          {/* Show subtle loading overlay during search */}
          {isSearching && searchLoading && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-luxury-red/0 via-luxury-red/50 to-luxury-red/0 animate-pulse z-10" />
          )}
          
          {displayItems.length === 0 ? (
            <EmptyChats
              searchTerm={searchTerm} 
              variant={variant}
            />
          ) : (
            <div className={listClasses}>
              {displayItems.map(chat => (
                <ChatListItem 
                  key={chat.uniqueKey || chat.id} 
                  chat={chat} 
                  onDelete={handleDeleteConversation}
                  onRestore={handleRestoreConversation}
                  variant={variant}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </OnboardingGuard>
  );
};

export default ChatList;
